import { useEffect, useRef, useState } from 'react'
import { Chip } from '@truepill/react-capsule'
import { Link, useLocation } from '@truepill/tpos-react-router'
import type { ApolloError } from '@truepill/tpos-react-router'
import type { BulkActionSourceQueue, BulkActionStepResultStatus } from '@truepill/tpos-types'
import LoadingSpinner from 'components/Loading'
import useErrorToast from 'hooks/toast/useErrorToast'
import { useAbortBulkAction } from 'hooks/useBulkActions'
import type { FulfillmentQueueName } from 'hooks/useFulfillmentQueue'
import { useModalContext } from 'providers/Overlays/ModalProvider'
import { useTPCacheContext } from 'providers/TPCacheProvider'
import { ChevronDown, ChevronUp, Slash } from 'react-feather'
import { goToFulfillmentOrder } from 'routes'
import styled from 'styled-components'
import {
  borderColor,
  capsuleDarkBlue,
  capsuleGrayColor,
  capsuleLightGreyColor,
  capsulePrimaryColorDark,
  capsuleRed,
  darkGreen,
  primaryColor,
} from 'styles/styleVariables'
import type { BulkAction, BulkActionStepGql } from 'types'
import {
  formatStepLabel,
  getBulkActionParamsSummary,
  getBulkActionLabel,
  getStepCounts,
  getStepState,
  isBulkActionAborted,
  isBulkActionTerminal,
  StepState,
} from 'utils/bulkActions'
import { formatDate } from 'utils/dates'
import AbortBulkActionModal from '../AbortBulkActionModal'

type StepRowProps = {
  step: BulkActionStepGql
  stepIndex: number
  state: StepState
  totalOrders: number
  sourceQueue?: BulkActionSourceQueue
  search: string
  getLocationNameById: (id: string) => string
}

const StepRow = ({ step, stepIndex, state, totalOrders, sourceQueue, search, getLocationNameById }: StepRowProps) => {
  const counts = getStepCounts(step, totalOrders)
  const summary = getBulkActionParamsSummary(step, getLocationNameById)
  const [expanded, setExpanded] = useState(state !== StepState.Completed || stepIndex === 0)
  const showResults =
    state === StepState.Running || state === StepState.Aborted || (state === StepState.Completed && expanded)
  const isToggleable = state === StepState.Completed || state === StepState.Aborted

  return (
    <StepBlock data-state={state} $pending={state === StepState.Pending}>
      <StepHeading
        type="button"
        $clickable={isToggleable}
        disabled={!isToggleable}
        aria-label={isToggleable ? (expanded ? 'Collapse step' : 'Expand step') : undefined}
        aria-expanded={isToggleable ? expanded : undefined}
        onClick={isToggleable ? () => setExpanded(v => !v) : undefined}
      >
        <strong>{formatStepLabel(stepIndex, step.type)}</strong>
        {summary ? <StepParams> — {summary}</StepParams> : null}
        <StepBadge state={state} counts={counts} />
        {isToggleable && <ToggleIcon>{expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</ToggleIcon>}
      </StepHeading>
      {state === StepState.Pending && <PendingSubtitle>Waiting for previous step to finish</PendingSubtitle>}
      {state === StepState.Aborted && counts.processed === 0 && <PendingSubtitle>Not run — aborted</PendingSubtitle>}
      {showResults && counts.processed > 0 && (
        <StepCounts>
          <StatusText status="success">✓ {counts.success}</StatusText>
          {counts.failed > 0 && <StatusText status="failed">✗ {counts.failed}</StatusText>}
          {counts.skipped > 0 && <StatusText status="skipped">⊘ {counts.skipped}</StatusText>}
        </StepCounts>
      )}
      {showResults && step.results.length > 0 && (
        <ResultList>
          {step.results.map(result => (
            <ResultRow
              key={`${step.type}-${result.orderId}`}
              to={goToFulfillmentOrder({
                fulfillmentQueueName: sourceQueue as FulfillmentQueueName | undefined,
                orderId: result.orderId.toString(),
                search,
              })}
            >
              <ResultStatus status={result.status}>
                {result.status === 'success' ? '✓' : result.status === 'failed' ? '✗' : '⊘'}
              </ResultStatus>
              <ResultOrderId>{result.orderId}</ResultOrderId>
              {result.error ? <ResultError>{result.error}</ResultError> : null}
            </ResultRow>
          ))}
        </ResultList>
      )}
    </StepBlock>
  )
}

type StepBadgeProps = {
  state: StepState
  counts: ReturnType<typeof getStepCounts>
}

const StepBadge = ({ state, counts }: StepBadgeProps): JSX.Element => {
  if (state === StepState.Pending) return <PendingBadge>Pending</PendingBadge>
  if (state === StepState.Running) {
    return (
      <Chip state="info" css={{ fontSize: '0.625rem' }}>
        Running {counts.processed}/{counts.total}
      </Chip>
    )
  }
  if (state === StepState.Aborted) {
    return (
      <Chip state="error" css={{ fontSize: '0.625rem' }}>
        Stopped {counts.processed}/{counts.total}
      </Chip>
    )
  }
  return (
    <Chip state="success" css={{ fontSize: '0.625rem' }}>
      Completed {counts.success ? `✓ ${counts.success}` : ''} {counts.failed ? `✗ ${counts.failed}` : ''}{' '}
      {counts.skipped ? `⊘ ${counts.skipped}` : ''}
    </Chip>
  )
}

type EntryCardProps = {
  entry: BulkAction
  defaultExpanded: boolean
}

const BulkActionEntryCard = ({ entry, defaultExpanded }: EntryCardProps): JSX.Element => {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const { getLocationNameById } = useTPCacheContext()
  const { search } = useLocation()
  const { showModal } = useModalContext()
  const { abortBulkAction, loading: aborting } = useAbortBulkAction()
  const showErrorToast = useErrorToast()
  const aborted = isBulkActionAborted(entry)
  const running = !isBulkActionTerminal(entry)
  const totalProcessed = entry.steps.reduce((sum, step) => sum + step.results.length, 0)
  const totalExpected = entry.steps.length * entry.orderIds.length

  const initiatorName = `${entry.user?.firstName ?? ''} ${entry.user?.lastName ?? ''}`.trim() || 'Unknown user'
  const actionsLabel = entry.steps.map(step => getBulkActionLabel(step.type)).join(' → ')

  const handleAbort = () => {
    showModal(() => (
      <AbortBulkActionModal
        actionsLabel={actionsLabel}
        orderCount={entry.orderIds.length}
        onConfirm={() => {
          abortBulkAction(entry._id.toString()).catch(err =>
            showErrorToast(`Failed to abort bulk action: ${err instanceof Error ? err.message : String(err)}`),
          )
        }}
      />
    ))
  }

  return (
    <EntryCard>
      <EntryHeader
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => setExpanded(value => !value)}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            setExpanded(value => !value)
          }
        }}
      >
        <EntryInfo>
          <EntryTitle>{actionsLabel}</EntryTitle>
          <EntryMeta>
            {running ? (
              <RunningChipWrapper>
                <Chip state="info" css={{ fontSize: '0.625rem' }}>
                  Running
                </Chip>
                <LoadingSpinner />
              </RunningChipWrapper>
            ) : aborted ? (
              <Chip state="error" css={{ fontSize: '0.625rem' }}>
                Aborted
              </Chip>
            ) : (
              <Chip state="success" css={{ fontSize: '0.625rem' }}>
                Completed
              </Chip>
            )}
            <EntryMetaText>{entry.orderIds.length} orders</EntryMetaText>
            <EntryMetaText>by {initiatorName}</EntryMetaText>
            <EntryMetaText>{formatDate(entry.createdAt, 'M/D/YYYY, h:mm:ss A')}</EntryMetaText>
            {(running || aborted) && totalExpected > 0 && (
              <EntryMetaText>
                {aborted ? 'Stopped ' : ''}
                {totalProcessed}/{totalExpected}
              </EntryMetaText>
            )}
          </EntryMeta>
        </EntryInfo>
        {running && (
          <AbortButton
            type="button"
            onClick={event => {
              event.stopPropagation()
              handleAbort()
            }}
            disabled={aborting}
          >
            <Slash size={13} />
            {aborting ? 'Aborting…' : 'Abort'}
          </AbortButton>
        )}
        <ExpandIcon>{expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</ExpandIcon>
      </EntryHeader>
      {expanded && (
        <EntryBody>
          {entry.steps.map((step, idx) => (
            <StepRow
              key={`${step.type}-${idx}`}
              step={step}
              stepIndex={idx}
              state={getStepState(entry, idx)}
              totalOrders={entry.orderIds.length}
              sourceQueue={entry.sourceQueue}
              search={search}
              getLocationNameById={getLocationNameById}
            />
          ))}
        </EntryBody>
      )}
    </EntryCard>
  )
}

type HistoryTabProps = {
  bulkActions: BulkAction[]
  loading: boolean
  loadingMore: boolean
  error?: ApolloError
  locationId?: string
  loadMore: () => void
  hasMore: boolean
}

const HistoryTab = ({ bulkActions, loading, loadingMore, error, locationId, loadMore, hasMore }: HistoryTabProps) => {
  const showErrorToast = useErrorToast()
  const lastErrorMessage = useRef<string>()

  useEffect(() => {
    if (!error?.message || lastErrorMessage.current === error.message) return
    lastErrorMessage.current = error.message
    showErrorToast(`Failed to load bulk actions history: ${error.message}`)
  }, [error?.message, showErrorToast])

  if (!locationId) {
    return <EmptyState>No location associated with your account</EmptyState>
  }

  if (loading && bulkActions.length === 0) {
    return <EmptyState>Loading…</EmptyState>
  }

  if (bulkActions.length === 0) {
    return <EmptyState>No bulk actions yet</EmptyState>
  }

  return (
    <HistoryScrollContainer>
      {bulkActions.map(entry => (
        <BulkActionEntryCard key={String(entry._id)} entry={entry} defaultExpanded={!isBulkActionTerminal(entry)} />
      ))}
      {hasMore && (
        <LoadMoreButton type="button" onClick={loadMore} disabled={loadingMore}>
          {loadingMore ? 'Loading…' : 'Load more'}
        </LoadMoreButton>
      )}
    </HistoryScrollContainer>
  )
}

export default HistoryTab

const EmptyState = styled.div`
  color: ${capsuleLightGreyColor};
  font-size: 13px;
  padding: 16px 0;
  text-align: center;
`

const HistoryScrollContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const LoadMoreButton = styled.button`
  margin: 4px auto 0;
  padding: 6px 16px;
  font-size: 12px;
  font-weight: 600;
  color: ${capsuleDarkBlue};
  background: transparent;
  border: 1px solid ${borderColor};
  border-radius: 6px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${borderColor};
  }

  &:disabled {
    cursor: default;
    color: ${capsuleLightGreyColor};
  }
`

const EntryCard = styled.div`
  border: 1px solid ${borderColor};
  border-radius: 6px;
  overflow: hidden;
`

const EntryHeader = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: ${borderColor};
  }
`

const EntryInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`

const EntryTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${capsulePrimaryColorDark};
`

const EntryMeta = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const EntryMetaText = styled.span`
  font-size: 12px;
  color: ${capsuleLightGreyColor};
`

const ExpandIcon = styled.span`
  display: flex;
  align-items: center;
  margin-left: 8px;
  color: ${capsuleLightGreyColor};
`

const AbortButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  margin-left: 8px;
  font-size: 12px;
  font-weight: 600;
  color: ${capsuleRed};
  background: transparent;
  border: 1px solid ${capsuleRed};
  border-radius: 4px;
  padding: 3px 10px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${capsuleRed};
    color: white;
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`

const EntryBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 12px 12px;
  border-top: 1px solid ${borderColor};
`

const StepBlock = styled.div<{ $pending: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  opacity: ${({ $pending }) => ($pending ? 0.5 : 1)};
`

const StepHeading = styled.button<{ $clickable?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  width: 100%;
  background: transparent;
  border: none;
  padding: 0;
  text-align: left;
  color: inherit;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  &:disabled {
    cursor: default;
    opacity: 1;
    color: inherit;
  }

  &:hover {
    color: ${({ $clickable }) => ($clickable ? capsuleDarkBlue : 'inherit')};
  }
`

const StepParams = styled.span`
  color: ${capsuleLightGreyColor};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const PendingBadge = styled.span`
  background: ${capsuleGrayColor};
  color: ${capsuleLightGreyColor};
  font-size: 0.625rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-left: auto;
`

const ToggleIcon = styled.span`
  color: ${capsuleLightGreyColor};
  display: flex;
  align-items: center;
  margin-left: auto;
`

const RunningChipWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;

  .LoadingAnimation {
    width: 14px;
    height: 14px;
  }
`

const PendingSubtitle = styled.span`
  font-size: 11px;
  font-style: italic;
  color: ${capsuleLightGreyColor};
`

const StepCounts = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  font-size: 11px;
`

const StatusText = styled.span<{ status: BulkActionStepResultStatus }>`
  color: ${({ status }) =>
    status === 'success' ? darkGreen : status === 'failed' ? capsuleRed : capsuleLightGreyColor};
`

const ResultList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 4px;
  max-height: 12rem;
  overflow-y: auto;
`

const ResultRow = styled(Link)`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: 6px;
  font-size: 11px;
  padding: 2px 4px;
  border-radius: 4px;
  text-decoration: none;
  color: inherit;

  &:hover {
    span {
      color: ${primaryColor};
    }
  }
`

const ResultStatus = styled(StatusText)`
  width: 12px;
  flex-shrink: 0;
`

const ResultOrderId = styled.span`
  font-family: monospace;
  color: ${capsuleDarkBlue};
`

const ResultError = styled.span`
  color: ${capsuleRed};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
